import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { QRCodeSVG } from 'qrcode.react';
import api from '../services/api';
import { usePermissions } from '../context/PermissionContext';
import { useAuth } from '../context/AuthContext';
import StatusBadge from '../components/StatusBadge';

const formatAmount = (value) => `$${Number(value || 0).toFixed(2)}`;
const formatDate = (value) => (value ? new Date(value).toLocaleDateString() : '-');
// E7 history rows carry times that matter (who acted when) — full timestamp.
const formatTimestamp = (value) => (value ? new Date(value).toLocaleString() : '-');
// For date-only values (expense_date): format the Y-M-D parts directly —
// new Date('YYYY-MM-DD') parses as UTC midnight and renders a day early in
// negative-offset timezones. Timestamps keep using formatDate.
const formatDateOnly = (value) => {
  if (!value) return '-';
  const [y, m, d] = String(value).split('T')[0].split('-');
  if (!y || !m || !d) return '-';
  return `${Number(m)}/${Number(d)}/${y}`;
};
const todayStr = () => new Date().toISOString().slice(0, 10);

const MAX_RECEIPT_BYTES = 15 * 1024 * 1024;
// Mirrors the backend allowlist (RECEIPT_MIME_EXT in routes/expenses.js) —
// the server re-enforces at presign and again from HeadObject at confirm.
const RECEIPT_MIMES = ['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'application/pdf'];
const RECEIPT_EXT_MIME = {
  jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png',
  webp: 'image/webp', heic: 'image/heic', pdf: 'application/pdf'
};
const RECEIPT_ACCEPT = '.jpg,.jpeg,.png,.webp,.heic,.pdf';
const formatBytes = (b) => {
  const n = Number(b);
  if (!n) return '-';
  return n >= 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`;
};

const EMPTY_ITEM_FORM = { category_id: '', expense_date: '', amount: '', description: '' };
const EDITABLE_STATUSES = ['draft', 'rejected'];

// Export download (Slice A4a) — the Accidents.js blob idiom, with one
// improvement per the A4 design: error responses arrive as blobs too, so the
// catch decodes the blob back to JSON to surface the server's message.
const downloadExport = async (path, filename) => {
  try {
    const response = await api.get(path, { responseType: 'blob' });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  } catch (err) {
    let message = 'Export failed — try again';
    try {
      message = JSON.parse(await err.response.data.text()).message || message;
    } catch (decodeErr) {
      // Non-JSON or network-level failure — keep the generic message.
    }
    toast.error(message);
  }
};

// One modal in create/edit mode (editingItem convention).
const LineItemModal = ({ editingItem, form, setForm, categories, saving, onSubmit, onClose }) => (
  <div className="modal-overlay" onClick={onClose}>
    <div className="modal" onClick={(e) => e.stopPropagation()}>
      <h2>{editingItem ? 'Edit Line Item' : 'Add Line Item'}</h2>
      <form onSubmit={onSubmit}>
        <div className="form-row">
          <div className="form-group">
            <label>Category *</label>
            <select
              value={form.category_id}
              onChange={(e) => setForm({ ...form, category_id: e.target.value })}
              required
            >
              <option value="" disabled>Select a category</option>
              {categories.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Date *</label>
            <input
              type="date"
              value={form.expense_date}
              max={todayStr()}
              onChange={(e) => setForm({ ...form, expense_date: e.target.value })}
              required
            />
          </div>
        </div>
        <div className="form-row">
          <div className="form-group">
            <label>Amount *</label>
            <input
              type="number"
              min="0.01"
              step="0.01"
              value={form.amount}
              onChange={(e) => setForm({ ...form, amount: e.target.value })}
              placeholder="0.00"
              required
            />
          </div>
          <div className="form-group">
            <label>Description</label>
            <input
              type="text"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Optional"
            />
          </div>
        </div>
        <div className="modal-actions">
          <button type="button" onClick={onClose}>Cancel</button>
          <button
            type="submit"
            className="btn-primary"
            disabled={saving || !form.category_id || !form.expense_date || !form.amount}
          >
            {saving ? 'Saving...' : editingItem ? 'Save Changes' : 'Add Item'}
          </button>
        </div>
      </form>
    </div>
  </div>
);

// QR handoff to a phone (Slice F4). Mints a single-use 5-minute token, shows
// the QR (URL built from window.location.origin — the mint response's
// scan_url is a convenience we deliberately don't use), counts down, polls
// every 2s, and cancels the session on close/unmount if still pending.
const QrHandoffModal = ({ reportId, onClose, onCompleted }) => {
  const [session, setSession] = useState(null);
  const [phase, setPhase] = useState('minting'); // minting | active | expired | error
  const [secondsLeft, setSecondsLeft] = useState(300);
  const stateRef = useRef({ session: null, phase: 'minting' });
  stateRef.current = { session, phase };

  const mint = useCallback(async () => {
    try {
      setPhase('minting');
      const res = await api.post(`/expenses/${reportId}/handoff`);
      setSession({ token: res.data.token, expires_at: res.data.expires_at });
      setPhase('active');
    } catch (err) {
      setPhase('error');
      toast.error(err.response?.data?.message || 'Failed to create scan session');
    }
  }, [reportId]);

  useEffect(() => { mint(); }, [mint]);

  // Cancel on unmount, fire-and-forget — only while still pending.
  useEffect(() => () => {
    const cur = stateRef.current;
    if (cur.session && cur.phase === 'active') {
      api.post(`/expenses/${reportId}/handoff/${cur.session.token}/cancel`).catch(() => {});
    }
  }, [reportId]);

  useEffect(() => {
    if (phase !== 'active' || !session) return undefined;
    const tick = setInterval(() => {
      const left = Math.max(0, Math.floor((new Date(session.expires_at) - Date.now()) / 1000));
      setSecondsLeft(left);
      // A1 belt-and-braces: the client countdown expires the modal even if a
      // poll response hasn't said so yet.
      if (left <= 0) setPhase('expired');
    }, 1000);
    const poll = setInterval(async () => {
      try {
        const res = await api.get(`/expenses/${reportId}/handoff/${session.token}`);
        if (res.data.status === 'completed') {
          setPhase('completed');
          onCompleted();
        } else if (res.data.status === 'expired' || res.data.status === 'cancelled') {
          setPhase('expired');
        }
      } catch (err) {
        // Poll errors never crash the modal — next tick retries.
      }
    }, 2000);
    return () => { clearInterval(tick); clearInterval(poll); };
  }, [phase, session, reportId, onCompleted]);

  const mmss = `${Math.floor(secondsLeft / 60)}:${String(secondsLeft % 60).padStart(2, '0')}`;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ textAlign: 'center' }}>
        <h2>Scan with your phone</h2>
        {phase === 'minting' && <div className="loading">Creating scan session...</div>}
        {phase === 'active' && session && (
          <>
            <p style={{ color: 'var(--text-secondary)', marginBottom: '12px' }}>
              Point your phone camera at the code, then photograph the receipt.
            </p>
            <div style={{ background: '#FFFFFF', display: 'inline-block', padding: '12px', borderRadius: '8px' }}>
              <QRCodeSVG value={`${window.location.origin}/scan/${session.token}`} size={220} />
            </div>
            <p style={{ marginTop: '12px', color: 'var(--text-secondary)' }}>
              Expires in <strong style={{ color: 'var(--gold-primary)' }}>{mmss}</strong>
            </p>
          </>
        )}
        {phase === 'expired' && (
          <>
            <p style={{ margin: '16px 0' }}>QR expired — generate a new one.</p>
            <button className="btn-primary" onClick={mint}>Generate a new QR</button>
          </>
        )}
        {phase === 'error' && (
          <>
            <p style={{ margin: '16px 0' }}>Couldn't create a scan session.</p>
            <button className="btn-primary" onClick={mint}>Try again</button>
          </>
        )}
        <div className="modal-actions">
          <button type="button" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
};

// Advisory only — nothing blocks, no undo affordance.
const WarningsModal = ({ warnings, onClose }) => (
  <div className="modal-overlay" onClick={onClose}>
    <div className="modal" onClick={(e) => e.stopPropagation()}>
      <h2>Submitted — heads-up for your approver</h2>
      {warnings.duplicates.length > 0 && (
        <div style={{ marginBottom: '16px' }}>
          <h3 style={{ color: 'var(--status-warning)', marginBottom: '8px' }}>Possible duplicates</h3>
          <p style={{ color: 'var(--text-secondary)', marginBottom: '8px' }}>
            These items match amounts and dates on your other reports:
          </p>
          {warnings.duplicates.map((d, i) => (
            <div key={i} style={{ padding: '6px 0', borderBottom: '1px solid var(--border-subtle)' }}>
              {d.title} — {formatAmount(d.amount)} on {formatDateOnly(d.expense_date)}
            </div>
          ))}
        </div>
      )}
      {warnings.budget.length > 0 && (
        <div style={{ marginBottom: '16px' }}>
          <h3 style={{ color: 'var(--status-warning)', marginBottom: '8px' }}>Over monthly limit</h3>
          {warnings.budget.map((b, i) => (
            <div key={i} style={{ padding: '6px 0' }}>
              {b.month}: {formatAmount(b.total)} total exceeds your {formatAmount(b.limit)} monthly limit
            </div>
          ))}
        </div>
      )}
      <div className="modal-actions">
        <button type="button" className="btn-primary" onClick={onClose}>OK</button>
      </div>
    </div>
  </div>
);

// E7: small typed badge for history-row marks (OBO / via phone scan /
// break-glass / blocked-attempt) — keeps the timeline scannable.
const HistoryMark = ({ color, children }) => (
  <span style={{
    display: 'inline-block', padding: '1px 6px', marginLeft: '6px',
    borderRadius: '10px', fontSize: '11px', border: `1px solid ${color}`, color
  }}>
    {children}
  </span>
);

const ExpenseDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasPermission, loading: permissionsLoading } = usePermissions();
  const { user } = useAuth();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [categories, setCategories] = useState([]);
  const [showItemModal, setShowItemModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [itemForm, setItemForm] = useState(EMPTY_ITEM_FORM);
  const [savingItem, setSavingItem] = useState(false);
  const [showTitleModal, setShowTitleModal] = useState(false);
  const [titleDraft, setTitleDraft] = useState('');
  const [savingTitle, setSavingTitle] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [warnings, setWarnings] = useState(null);
  const [uploadingReceipt, setUploadingReceipt] = useState(false);
  const [pendingReceipt, setPendingReceipt] = useState(null); // { file, contentType }
  const [pendingLinkItem, setPendingLinkItem] = useState('');
  const [linkTarget, setLinkTarget] = useState(null); // receipt being (re)linked
  const [linkSelection, setLinkSelection] = useState('');
  const [linking, setLinking] = useState(false);
  const [receiptToDelete, setReceiptToDelete] = useState(null);
  const [deletingReceipt, setDeletingReceipt] = useState(false);
  const [showQrModal, setShowQrModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingReport, setDeletingReport] = useState(false);
  const [decisionModal, setDecisionModal] = useState(null); // null | 'approve' | 'reject'
  const [decisionNotes, setDecisionNotes] = useState('');
  const [deciding, setDeciding] = useState(false);
  const [showPayConfirm, setShowPayConfirm] = useState(false);
  const [paying, setPaying] = useState(false);
  // P1: optional check number typed at pay time (shared by the pay confirm
  // and the break-glass self-pay), plus the edit-after affordance.
  const [payReference, setPayReference] = useState('');
  const [showRefModal, setShowRefModal] = useState(false);
  const [refDraft, setRefDraft] = useState('');
  const [savingRef, setSavingRef] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [showVoidModal, setShowVoidModal] = useState(false);
  const [breakGlass, setBreakGlass] = useState(null); // null | 'approve' | 'pay'
  const [glassReason, setGlassReason] = useState('');
  const [breaking, setBreaking] = useState(false);
  const [voidReason, setVoidReason] = useState('');
  const [voiding, setVoiding] = useState(false);
  const [activeView, setActiveView] = useState('details'); // 'details' | 'history' (E7, manage-only)
  const [history, setHistory] = useState(null); // { events, views_summary }
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState(false);
  const receiptInputRef = useRef(null);

  const fetchReport = useCallback(async () => {
    try {
      setLoading(true);
      setNotFound(false);
      setLoadError(false);
      const response = await api.get(`/expenses/${id}`);
      setReport(response.data || null);
    } catch (err) {
      // 404 covers both nonexistent and not-yours — same rendering, no distinction.
      if (err.response?.status === 404) {
        setNotFound(true);
      } else {
        setLoadError(true);
        toast.error('Failed to load expense report');
      }
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await api.get('/expenses/categories');
        setCategories(Array.isArray(response.data) ? response.data : []);
      } catch (err) {
        toast.error('Failed to load categories');
      }
    };
    fetchCategories();
  }, []);

  // E7: fetched on every History-tab activation — the trail is cheap and a
  // fresh fetch means new events (a decision, an export) appear without a
  // page reload. Server pre-collapses view rows into views_summary.
  const fetchHistory = useCallback(async () => {
    try {
      setHistoryLoading(true);
      setHistoryError(false);
      const response = await api.get(`/expenses/${id}/history`);
      setHistory(response.data || null);
    } catch (err) {
      setHistoryError(true);
    } finally {
      setHistoryLoading(false);
    }
  }, [id]);

  const openHistory = () => {
    setActiveView('history');
    fetchHistory();
  };

  // A 409 means the report's status changed under us — surface the server's
  // message and refetch so the UI snaps to the real state.
  const handleMutationError = (err, fallback) => {
    const message = err.response?.data?.message || fallback;
    toast.error(message);
    if (err.response?.status === 409) fetchReport();
  };

  const openAddItem = () => {
    setEditingItem(null);
    setItemForm({ ...EMPTY_ITEM_FORM, expense_date: todayStr() });
    setShowItemModal(true);
  };

  const openEditItem = (item) => {
    setEditingItem(item);
    setItemForm({
      category_id: item.category_id,
      expense_date: item.expense_date,
      amount: item.amount,
      description: item.description || ''
    });
    setShowItemModal(true);
  };

  const handleItemSubmit = async (e) => {
    e.preventDefault();
    try {
      setSavingItem(true);
      const body = {
        category_id: itemForm.category_id,
        expense_date: itemForm.expense_date,
        amount: itemForm.amount,
        description: itemForm.description.trim() || undefined
      };
      if (editingItem) {
        await api.put(`/expenses/${id}/items/${editingItem.id}`, body);
        toast.success('Item updated');
      } else {
        await api.post(`/expenses/${id}/items`, body);
        toast.success('Item added');
      }
      setShowItemModal(false);
      fetchReport();
    } catch (err) {
      handleMutationError(err, 'Failed to save line item');
      if (err.response?.status === 409) setShowItemModal(false);
    } finally {
      setSavingItem(false);
    }
  };

  const handleItemDelete = async (item) => {
    const linkedCount = (report?.receipts || []).filter((r) => r.line_item_id === item.id).length;
    const linkedNote = linkedCount > 0
      ? ` ${linkedCount} linked receipt${linkedCount === 1 ? '' : 's'} will become unlinked (not deleted).`
      : '';
    if (!window.confirm(`Delete this line item?${linkedNote}`)) return;
    try {
      await api.delete(`/expenses/${id}/items/${item.id}`);
      toast.success('Item deleted');
      fetchReport();
    } catch (err) {
      handleMutationError(err, 'Failed to delete line item');
    }
  };

  const handleTitleSave = async (e) => {
    e.preventDefault();
    if (!titleDraft.trim()) return;
    try {
      setSavingTitle(true);
      await api.put(`/expenses/${id}`, { title: titleDraft.trim() });
      toast.success('Title updated');
      setShowTitleModal(false);
      fetchReport();
    } catch (err) {
      handleMutationError(err, 'Failed to update title');
      if (err.response?.status === 409) setShowTitleModal(false);
    } finally {
      setSavingTitle(false);
    }
  };

  const handleSubmitReport = async () => {
    try {
      setSubmitting(true);
      const response = await api.post(`/expenses/${id}/submit`);
      const w = response.data?.warnings || { duplicates: [], budget: [] };
      if ((w.duplicates?.length || 0) === 0 && (w.budget?.length || 0) === 0) {
        toast.success('Report submitted');
      } else {
        setWarnings({ duplicates: w.duplicates || [], budget: w.budget || [] });
      }
      fetchReport();
    } catch (err) {
      handleMutationError(err, 'Failed to submit report');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReceiptPick = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!file) return;
    // Client-side fast-fail BEFORE any network call — the server re-enforces
    // both checks at presign and again from HeadObject at confirm.
    if (file.size > MAX_RECEIPT_BYTES) {
      toast.error('Receipt is too large — the limit is 15 MB');
      return;
    }
    const ext = (file.name.split('.').pop() || '').toLowerCase();
    const contentType = RECEIPT_MIMES.includes(file.type) ? file.type : RECEIPT_EXT_MIME[ext];
    if (!contentType) {
      toast.error('Receipts must be JPEG, PNG, WebP, HEIC, or PDF');
      return;
    }
    // E2: stash the validated file and open the upload modal (optional
    // line-item link) — the network flow runs from handleReceiptUpload.
    setPendingLinkItem('');
    setPendingReceipt({ file, contentType });
  };

  const handleReceiptUpload = async () => {
    if (!pendingReceipt) return;
    const { file, contentType } = pendingReceipt;
    try {
      setUploadingReceipt(true);
      const presign = await api.post(`/expenses/${id}/receipts/presign`, {
        content_type: contentType,
        file_size: file.size
      });
      const { upload_url, s3_key } = presign.data || {};
      // Raw fetch, not the api client: the presigned URL is a full S3 URL and
      // must not receive the auth header (same as Accidents.js presigned PUTs).
      const put = await fetch(upload_url, {
        method: 'PUT',
        body: file,
        headers: { 'Content-Type': contentType }
      });
      if (!put.ok) throw new Error('upload-failed');
      await api.post(`/expenses/${id}/receipts`, {
        s3_key,
        original_filename: file.name,
        ...(pendingLinkItem && { line_item_id: pendingLinkItem })
      });
      toast.success('Receipt uploaded');
      setPendingReceipt(null);
      fetchReport();
    } catch (err) {
      if (err.message === 'upload-failed' || !err.response) {
        // Non-ok PUT response, or a network/CORS-level fetch rejection
        // (TypeError — carries no err.response). Both are the PUT leg;
        // presign/confirm errors always carry err.response.
        toast.error('Upload failed — try again');
      } else if (err.response.status === 503) {
        toast.error(err.response.data?.message || 'Receipt storage is unavailable');
      } else {
        handleMutationError(err, 'Failed to upload receipt');
      }
    } finally {
      setUploadingReceipt(false);
    }
  };

  const handleReceiptLink = async () => {
    if (!linkTarget) return;
    try {
      setLinking(true);
      await api.patch(`/expenses/${id}/receipts/${linkTarget.id}`, {
        line_item_id: linkSelection || null
      });
      toast.success(linkSelection ? 'Receipt linked' : 'Receipt unlinked');
      setLinkTarget(null);
      fetchReport();
    } catch (err) {
      setLinkTarget(null);
      handleMutationError(err, 'Failed to link receipt');
    } finally {
      setLinking(false);
    }
  };

  const handleReceiptDownload = async (receipt) => {
    try {
      const response = await api.get(`/expenses/${id}/receipts/${receipt.id}/download`);
      if (response.data?.download_url) {
        window.open(response.data.download_url, '_blank', 'noopener');
      }
    } catch (err) {
      handleMutationError(err, 'Failed to get download link');
    }
  };

  const handleReceiptDelete = async () => {
    if (!receiptToDelete) return;
    try {
      setDeletingReceipt(true);
      await api.delete(`/expenses/${id}/receipts/${receiptToDelete.id}`);
      toast.success('Receipt deleted');
      setReceiptToDelete(null);
      fetchReport();
    } catch (err) {
      handleMutationError(err, 'Failed to delete receipt');
      if (err.response?.status === 409) setReceiptToDelete(null);
    } finally {
      setDeletingReceipt(false);
    }
  };

  // Decision handlers (Slice A2). Success stays on the page — the natural
  // next action (approve → pay, or reviewing the new state) is here. 409 =
  // another decider raced us; 403 = SoD (server writes the blocked audit
  // row) — both surface the server message, 409 also refetch-snaps.
  const handleDecision = async () => {
    if (decisionModal === 'reject' && !decisionNotes.trim()) return;
    const action = decisionModal;
    try {
      setDeciding(true);
      if (action === 'approve') {
        await api.post(`/expenses/${id}/approve`,
          decisionNotes.trim() ? { notes: decisionNotes.trim() } : {});
        toast.success('Report approved');
      } else {
        await api.post(`/expenses/${id}/reject`, { notes: decisionNotes.trim() });
        toast.success('Report rejected');
      }
      setDecisionModal(null);
      setDecisionNotes('');
      fetchReport();
    } catch (err) {
      setDecisionModal(null);
      handleMutationError(err, `Failed to ${action} report`);
    } finally {
      setDeciding(false);
    }
  };

  // P1: the check number is sent only when typed — an empty field keeps the
  // request byte-identical to pre-P1 (the server normalizes; blank → null).
  const payBody = () => (payReference.trim() ? { payment_reference: payReference.trim() } : {});

  const handlePay = async () => {
    try {
      setPaying(true);
      await api.post(`/expenses/${id}/pay`, payBody());
      toast.success('Marked as paid');
      setShowPayConfirm(false);
      setPayReference('');
      fetchReport();
    } catch (err) {
      setShowPayConfirm(false);
      handleMutationError(err, 'Failed to mark as paid');
    } finally {
      setPaying(false);
    }
  };

  // E6 break-glass: the SoD norm holds unless the involved decision-holder
  // explicitly self-decides with a reason; the report is permanently flagged
  // and peers are notified (client-approved question #4).
  const handleBreakGlass = async () => {
    if (!breakGlass || !glassReason.trim()) return;
    try {
      setBreaking(true);
      await api.post(`/expenses/${id}/${breakGlass}`, {
        self_approve: true,
        reason: glassReason.trim(),
        ...(breakGlass === 'pay' ? payBody() : {})
      });
      toast.success(breakGlass === 'approve' ? 'Self-approved — peers notified' : 'Self-paid — peers notified');
      setBreakGlass(null);
      setGlassReason('');
      setPayReference('');
      fetchReport();
    } catch (err) {
      setBreakGlass(null);
      handleMutationError(err, 'Break-glass action failed');
    } finally {
      setBreaking(false);
    }
  };

  // E5: void a PAID report — SoD-EXEMPT by gate ruling (any approve-holder,
  // including the filer/owner, may void; the server records actor + reason).
  const handleVoid = async () => {
    if (!voidReason.trim()) return;
    try {
      setVoiding(true);
      await api.post(`/expenses/${id}/void`, { reason: voidReason.trim() });
      toast.success('Report voided');
      setShowVoidModal(false);
      setVoidReason('');
      fetchReport();
    } catch (err) {
      setShowVoidModal(false);
      handleMutationError(err, 'Failed to void report');
    } finally {
      setVoiding(false);
    }
  };

  // P1: edit-after — approve-holders correct the recorded check number on a
  // paid OR voided report; blank clears it. The server audits from → to and
  // treats an unchanged value as a no-op. No SoD (a bookkeeping fact).
  const handleSaveReference = async () => {
    try {
      setSavingRef(true);
      await api.patch(`/expenses/${id}/payment-reference`, { payment_reference: refDraft.trim() });
      toast.success('Check number updated');
      setShowRefModal(false);
      fetchReport();
    } catch (err) {
      setShowRefModal(false);
      handleMutationError(err, 'Failed to update check number');
    } finally {
      setSavingRef(false);
    }
  };

  // On success we navigate away (no refetch — the report is gone). On 409 the
  // report still exists (e.g. submitted from another tab), so
  // handleMutationError's refetch snaps the UI to the real state.
  // Filename mirrors the server's Content-Disposition (the <a download>
  // attribute is what actually names the file in the blob idiom).
  const handleExportPdf = async () => {
    const slug = String(report?.title || '').toLowerCase()
      .replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 40) || 'report';
    setExportingPdf(true);
    await downloadExport(
      `/expenses/${id}/export.pdf`,
      `expense-report-${slug}-${String(id).slice(0, 8)}.pdf`
    );
    setExportingPdf(false);
  };

  const handleReportDelete = async () => {
    try {
      setDeletingReport(true);
      await api.delete(`/expenses/${id}`);
      toast.success('Draft deleted');
      navigate('/expenses');
    } catch (err) {
      setDeletingReport(false);
      setShowDeleteConfirm(false);
      handleMutationError(err, 'Failed to delete draft');
    }
  };

  if (loading) {
    return <div className="page-container"><div className="loading">Loading expense report...</div></div>;
  }

  if (loadError) {
    return (
      <div className="page-container">
        <div className="page-header"><h1>Expenses</h1></div>
        <div className="loading">
          <p>Couldn't load this report.</p>
          <button className="btn-primary" onClick={fetchReport}>Retry</button>
          <button className="btn-small" style={{ marginLeft: '8px' }} onClick={() => navigate('/expenses')}>
            Back to Expenses
          </button>
        </div>
      </div>
    );
  }

  if (notFound || !report) {
    return (
      <div className="page-container">
        <div className="page-header"><h1>Expenses</h1></div>
        <div className="loading">
          <p>Report not found.</p>
          <button className="btn-primary" onClick={() => navigate('/expenses')}>Back to Expenses</button>
        </div>
      </div>
    );
  }

  const items = report.line_items || [];
  const receipts = report.receipts || [];
  const itemSummary = (item) =>
    `${formatDateOnly(item.expense_date)} — ${item.category_name} — ${formatAmount(item.amount)}`;
  const itemsById = {};
  for (const it of items) itemsById[it.id] = it;
  const receiptCountByItem = {};
  for (const r of receipts) {
    if (r.line_item_id) receiptCountByItem[r.line_item_id] = (receiptCountByItem[r.line_item_id] || 0) + 1;
  }
  // E4 pre-submit cue: the server 400 is the enforcement — this line just
  // tells the filer before they hit it. Zero receipts is fine (must-link,
  // not must-have).
  const unlinkedReceipts = receipts.filter((r) => !r.line_item_id).length;
  // EXP1a must-have cue (owner rulings 1/4): every item needs at least one
  // linked receipt. Same derivation and same order as the server check at
  // submit, so the names here read exactly like the 400 the filer would get.
  // Advisory only — the submit button stays enabled and the server 400 is the
  // enforcement, matching how the E4 cue above works.
  const itemsMissingReceipt = items.filter((it) => !receiptCountByItem[it.id]);
  // E2 refinement (rode along with A4a per the ratified plan): annotate link
  // options with their current receipt count — already-linked items stay
  // selectable (many-to-one is legal), the count just makes it visible.
  const itemOptionLabel = (it) => {
    const n = receiptCountByItem[it.id] || 0;
    return n ? `${itemSummary(it)} (${n} receipt${n === 1 ? '' : 's'})` : itemSummary(it);
  };
  const isEditableStatus = EDITABLE_STATUSES.includes(report.status);
  const canEdit = hasPermission('expenses', 'create') && isEditableStatus;
  const canSubmit = hasPermission('expenses', 'submit') && isEditableStatus;
  const submitLabel = report.status === 'rejected' ? 'Resubmit' : 'Submit for Approval';
  // SoD-aware decision visibility (Slice A2): the server 403 remains the real
  // guard — hiding is UX, the note surfaces WHY an involved approver can't act.
  const isOwnerOrFiler = user?.id === report.employee_id || user?.id === report.created_by;
  const canDecide = hasPermission('expenses', 'approve') && !isOwnerOrFiler;
  const showSodNote = hasPermission('expenses', 'approve') && isOwnerOrFiler
    && (report.status === 'submitted' || report.status === 'approved');

  // E7: History tab is manage-scope ONLY (owner ruling — the trail is an
  // internal-controls instrument, not a transparency feed to owners). The
  // permissionsLoading guard keeps the fail-open loading flash from briefly
  // rendering the tab button; the server 403 is the real gate.
  const canSeeHistory = !permissionsLoading && hasPermission('expenses', 'manage');
  const catNameById = {};
  for (const c of categories) catNameById[c.id] = c.name;
  // E7b (D-B): no-op pairs (the edit modal PUTs every field) are filtered at
  // render time — the audit row is untouched; category names come from the
  // server's enrichment (from_name/to_name — retired categories included),
  // the active-only map is the fallback; the item is referenced by its
  // current line, or named as since-deleted.
  const isNoopChange = (c) => String(c?.from ?? '') === String(c?.to ?? '');
  const changedFieldText = (changed) => Object.entries(changed || {}).filter(([, c]) => !isNoopChange(c)).map(([k, c]) => {
    if (k === 'category_id') {
      return `category ${c.from_name || catNameById[c.from] || 'unknown'} → ${c.to_name || catNameById[c.to] || 'unknown'}`;
    }
    if (k === 'amount') return `amount ${formatAmount(c.from)} → ${formatAmount(c.to)}`;
    if (k === 'expense_date') return `date ${formatDateOnly(c.from)} → ${formatDateOnly(c.to)}`;
    if (k === 'description') return `description "${c.from ?? ''}" → "${c.to ?? ''}"`;
    return `${k} ${c.from ?? '—'} → ${c.to ?? '—'}`;
  }).join('; ');
  const historyItemRef = (d) => (itemsById[d.item_id] ? itemSummary(itemsById[d.item_id]) : 'an item since deleted');
  // One renderer per action type — reasons inline, marks for the governance
  // facts (OBO / via phone scan / break-glass / blocked attempt).
  const renderHistoryEvent = (e) => {
    const d = e.details || {};
    switch (e.action) {
      case 'create':
        return <>Report created{d.on_behalf_of && (
          <> for {d.employee_name || 'employee'}<HistoryMark color="var(--status-info)">on behalf of</HistoryMark></>
        )}</>;
      case 'edit':
        return d.changed?.title
          ? <>Title changed: "{d.changed.title.from}" → "{d.changed.title.to}"</>
          : <>Report edited</>;
      case 'add_item':
        return <>Item added — {formatAmount(d.amount)}{(d.category_name || catNameById[d.category_id]) ? ` (${d.category_name || catNameById[d.category_id]})` : ''} on {formatDateOnly(d.expense_date)}</>;
      case 'edit_item':
        return <>Item edited — {historyItemRef(d)}: {changedFieldText(d.changed) || 'no field changes'}</>;
      case 'delete_item':
        return <>Item deleted — {formatAmount(d.amount)} on {formatDateOnly(d.expense_date)}</>;
      case 'upload_receipt':
        return <>Receipt uploaded{d.line_item_id ? ', linked to an item' : ''}{d.via_handoff && (
          <HistoryMark color="var(--text-secondary)">via phone scan</HistoryMark>
        )}</>;
      case 'download_receipt':
        return <>Receipt downloaded</>;
      case 'delete_receipt':
        return <>Receipt deleted</>;
      case 'link_receipt':
        return <>{d.to ? (d.from ? 'Receipt re-linked to another item' : 'Receipt linked to an item') : 'Receipt unlinked from its item'}</>;
      case 'submit':
        return <>{d.resubmission ? 'Resubmitted' : 'Submitted'} for approval
          {(d.months_over_budget || []).length > 0 && (
            <span style={{ color: 'var(--status-warning)' }}> — over budget: {d.months_over_budget.join(', ')}</span>
          )}
          {d.duplicate_count > 0 && <> — {d.duplicate_count} possible duplicate item{d.duplicate_count === 1 ? '' : 's'} flagged</>}
        </>;
      case 'approve':
        return <>Approved{d.self_approved && (
          <HistoryMark color="#A78BFA">SELF-APPROVED: {d.reason}</HistoryMark>
        )}{d.notes ? <> — "{d.notes}"</> : null}</>;
      case 'reject':
        return <>Rejected — "{d.notes}"</>;
      case 'pay':
        return <>Marked paid{d.payment_reference ? <> — Check #{d.payment_reference}</> : null}{d.self_action && (
          <HistoryMark color="#A78BFA">SELF (break-glass): {d.reason}</HistoryMark>
        )}</>;
      case 'edit_payment_reference':
        // P1: set / changed / cleared read differently on purpose.
        return d.to
          ? (d.from ? <>Check number changed: {d.from} → {d.to}</> : <>Check number set: {d.to}</>)
          : <>Check number cleared (was {d.from ?? '—'})</>;
      case 'void':
        return <>Voided — "{d.reason}"</>;
      case 'self_decision_blocked':
        // Neutral, rule-centered copy (gate ruling): the subject is the rule
        // firing, not the person.
        return <>{d.attempted || 'Decision'} attempt<HistoryMark color="var(--status-danger)">blocked — separation of duties</HistoryMark></>;
      case 'export':
        return <>Report PDF exported ({d.embedded_receipts ?? 0} of {d.receipt_count ?? 0} receipt{d.receipt_count === 1 ? '' : 's'} embedded)</>;
      default:
        return <>{e.action}</>;
    }
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>
          {report.title}
          {canEdit && (
            <button
              className="btn-small"
              style={{ marginLeft: '12px', verticalAlign: 'middle' }}
              onClick={() => { setTitleDraft(report.title); setShowTitleModal(true); }}
            >
              Edit
            </button>
          )}
        </h1>
        <div style={{ display: 'flex', gap: '8px' }}>
          {canSubmit && unlinkedReceipts > 0 && (
            <span style={{ color: 'var(--status-warning)', alignSelf: 'center' }}>
              {unlinkedReceipts} receipt{unlinkedReceipts === 1 ? " isn't" : "s aren't"} linked
              to a line item — link {unlinkedReceipts === 1 ? 'it' : 'them'} before submitting.
            </span>
          )}
          {canSubmit && itemsMissingReceipt.length > 0 && (
            <span style={{ color: 'var(--status-warning)', alignSelf: 'center' }}>
              {itemsMissingReceipt.length} item{itemsMissingReceipt.length === 1 ? ' has' : 's have'} no
              receipt: {itemsMissingReceipt.map((it) => `${it.category_name} ${formatAmount(it.amount)}`).join(', ')}
              {' '}— attach {itemsMissingReceipt.length === 1 ? 'one' : 'them'} before submitting.
            </span>
          )}
          {canSubmit && (
            <button
              className="btn-primary"
              disabled={submitting || items.length === 0}
              title={items.length === 0 ? 'Add at least one line item before submitting' : undefined}
              onClick={handleSubmitReport}
            >
              {submitting ? 'Submitting...' : submitLabel}
            </button>
          )}
          {canDecide && report.status === 'submitted' && (
            <>
              <button
                className="btn-primary"
                onClick={() => { setDecisionNotes(''); setDecisionModal('approve'); }}
              >
                Approve
              </button>
              <button
                className="btn-danger"
                onClick={() => { setDecisionNotes(''); setDecisionModal('reject'); }}
              >
                Reject
              </button>
            </>
          )}
          {canDecide && report.status === 'approved' && (
            <button className="btn-primary" onClick={() => { setPayReference(''); setShowPayConfirm(true); }}>
              Mark as Paid
            </button>
          )}
          {hasPermission('expenses', 'approve') && report.status === 'paid' && (
            <button className="btn-danger" onClick={() => { setVoidReason(''); setShowVoidModal(true); }}>
              Void Report
            </button>
          )}
          {showSodNote && (
            <>
              <span style={{ color: 'var(--text-secondary)', alignSelf: 'center' }}>
                You filed this report — another approver must decide it.
              </span>
              {report.status === 'submitted' && (
                <button className="btn-small btn-danger" onClick={() => { setGlassReason(''); setBreakGlass('approve'); }}>
                  Self-approve anyway…
                </button>
              )}
              {report.status === 'approved' && (
                <button className="btn-small btn-danger" onClick={() => { setGlassReason(''); setPayReference(''); setBreakGlass('pay'); }}>
                  Mark paid anyway…
                </button>
              )}
            </>
          )}
          {hasPermission('expenses', 'create') && report.status === 'draft' && (
            <button className="btn-danger" onClick={() => setShowDeleteConfirm(true)}>
              Delete Draft
            </button>
          )}
          {hasPermission('expenses', 'export') && (
            <button className="btn-small" disabled={exportingPdf} onClick={handleExportPdf}>
              {exportingPdf ? 'Exporting…' : 'Download PDF'}
            </button>
          )}
          <button className="btn-small" onClick={() => navigate('/expenses')}>← Back</button>
        </div>
      </div>

      {report.self_approved && (
        <div style={{
          background: 'rgba(124, 58, 237, 0.12)',
          border: '1px solid #A78BFA',
          borderRadius: '8px',
          padding: '12px 16px',
          marginBottom: '16px',
          color: 'var(--text-primary)'
        }}>
          <strong>⚠ SELF-APPROVED (break-glass)</strong>
          {report.decided_by_name ? ` by ${report.decided_by_name}` : ''}: {report.self_approval_reason}
          <div style={{ color: 'var(--text-secondary)', marginTop: '4px' }}>
            Separation of duties was bypassed with an explicit reason. Other
            decision-holders were notified. This flag is permanent.
          </div>
        </div>
      )}

      {report.status === 'voided' && (
        <div style={{
          background: 'var(--status-danger-bg)',
          border: '1px solid var(--status-danger)',
          borderRadius: '8px',
          padding: '12px 16px',
          marginBottom: '16px',
          color: 'var(--text-primary)'
        }}>
          <strong>Voided</strong>
          {report.voided_by_name ? ` (${report.voided_by_name}, ${formatDate(report.voided_at)})` : ''}: {report.void_reason}
          <div style={{ color: 'var(--text-secondary)', marginTop: '4px' }}>
            Excluded from totals and exports. The report and its history remain visible.
          </div>
        </div>
      )}

      {report.status === 'rejected' && report.decision_notes && (
        <div style={{
          background: 'var(--status-danger-bg)',
          border: '1px solid var(--status-danger)',
          borderRadius: '8px',
          padding: '12px 16px',
          marginBottom: '16px',
          color: 'var(--text-primary)'
        }}>
          <strong>Rejected — fix and resubmit</strong>
          {report.decided_by_name ? ` (${report.decided_by_name}, ${formatDate(report.decided_at)})` : ''}: {report.decision_notes}
        </div>
      )}

      {report.status === 'submitted' && report.decided_at && (
        <div style={{
          background: 'var(--status-info-bg)',
          border: '1px solid var(--status-info)',
          borderRadius: '8px',
          padding: '12px 16px',
          marginBottom: '16px',
          color: 'var(--text-primary)'
        }}>
          <strong>Previous decision:</strong> rejected {formatDate(report.decided_at)}
          {report.decided_by_name ? ` by ${report.decided_by_name}` : ''}
          {report.decision_notes ? ` — "${report.decision_notes}"` : ''}. This report was revised and resubmitted.
        </div>
      )}

      {report.budget_status && report.budget_status.months?.some((m) => m.over) && (
        <div style={{
          background: 'var(--gold-light)',
          border: '1px solid var(--gold-primary)',
          borderRadius: '8px',
          padding: '12px 16px',
          marginBottom: '16px',
          color: 'var(--text-primary)'
        }}>
          <strong>⚠ Over monthly limit</strong>
          {report.budget_status.months.filter((m) => m.over).map((m) => (
            <div key={m.month} style={{ marginTop: '4px' }}>
              {m.month}: {formatAmount(m.month_total)} total for {report.employee_name || 'this employee'} (this
              report: {formatAmount(m.report_total)}) exceeds
              the {formatAmount(report.budget_status.monthly_limit)} monthly
              limit ({report.budget_status.is_custom ? 'custom' : 'default'}).
            </div>
          ))}
          <div style={{ color: 'var(--text-secondary)', marginTop: '4px' }}>Budget warnings are advisory.</div>
        </div>
      )}

      <div className="table-container" style={{ padding: '16px', marginBottom: '16px' }}>
        <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap', alignItems: 'center' }}>
          <span><strong>Employee:</strong> {report.employee_name || '-'}</span>
          <StatusBadge status={report.status} />
          <span><strong>Total:</strong> {formatAmount(report.total_amount)}</span>
          <span><strong>Created:</strong> {formatDate(report.created_at)}</span>
          {report.status === 'submitted' && (
            <span style={{ color: 'var(--text-secondary)' }}>
              Awaiting review — submitted {formatDate(report.submitted_at)}
            </span>
          )}
          {(report.status === 'approved' || report.status === 'paid' || report.status === 'voided') && (
            <span style={{ color: 'var(--text-secondary)' }}>
              Approved {formatDate(report.decided_at)}
              {report.decided_by_name ? ` by ${report.decided_by_name}` : ''}
              {report.self_approved ? ' (self-approved)' : ''}
              {report.decision_notes ? ` — ${report.decision_notes}` : ''}
            </span>
          )}
          {(report.status === 'paid' || report.status === 'voided') && report.paid_at && (
            <span style={{ color: 'var(--text-secondary)' }}>
              Paid {formatDate(report.paid_at)}
              {report.paid_by_name ? ` by ${report.paid_by_name}` : ''}
              {report.payment_reference ? ` — Check #${report.payment_reference}` : ''}
              {/* P1: edit-after for approve-holders, paid AND voided (the
                  facts survive a void and must stay correctable). */}
              {hasPermission('expenses', 'approve') && (
                <button
                  type="button"
                  className="btn-small"
                  style={{ marginLeft: '8px' }}
                  onClick={() => { setRefDraft(report.payment_reference || ''); setShowRefModal(true); }}
                >
                  Edit check number
                </button>
              )}
            </span>
          )}
        </div>
      </div>

      {canSeeHistory && (
        <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
          <button
            className={activeView === 'details' ? 'btn-primary btn-small' : 'btn-small'}
            onClick={() => setActiveView('details')}
          >
            Details
          </button>
          <button
            className={activeView === 'history' ? 'btn-primary btn-small' : 'btn-small'}
            onClick={openHistory}
          >
            History
          </button>
        </div>
      )}

      {canSeeHistory && activeView === 'history' && (
        historyLoading ? (
          <div className="loading">Loading history...</div>
        ) : historyError ? (
          <div className="loading">
            <p>Failed to load history.</p>
            <button className="btn-small" onClick={fetchHistory}>Retry</button>
          </div>
        ) : !history || (history.events.length === 0 && !history.views_summary) ? (
          <div className="loading">No history recorded.</div>
        ) : (
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Who</th>
                  <th>Event</th>
                </tr>
              </thead>
              <tbody>
                {history.events.map((e) => (
                  <tr key={e.id}>
                    <td style={{ whiteSpace: 'nowrap' }}>{formatTimestamp(e.created_at)}</td>
                    <td>{e.actor_name || '(deleted user)'}</td>
                    <td>{renderHistoryEvent(e)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {history.views_summary && (
              <p style={{ color: 'var(--text-secondary)', padding: '8px 12px', margin: 0 }}>
                Viewed {history.views_summary.count} time{history.views_summary.count === 1 ? '' : 's'} by{' '}
                {history.views_summary.viewers.join(', ')} — first {formatTimestamp(history.views_summary.first_at)},
                last {formatTimestamp(history.views_summary.last_at)}.
              </p>
            )}
          </div>
        )
      )}

      {(!canSeeHistory || activeView === 'details') && (
      <>
      <div className="page-header" style={{ marginBottom: '8px' }}>
        <h2 style={{ margin: 0 }}>Line Items</h2>
        {canEdit && (
          <button className="btn-primary" onClick={openAddItem}>+ Add Item</button>
        )}
      </div>

      {items.length === 0 ? (
        <div className="loading">
          <p>No line items yet.</p>
          {canEdit && (
            <button className="btn-primary" onClick={openAddItem}>Add the first item</button>
          )}
        </div>
      ) : (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Category</th>
                <th>Description</th>
                <th>Amount</th>
                {canEdit && <th>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id}>
                  <td>{formatDateOnly(item.expense_date)}</td>
                  <td>{item.category_name}</td>
                  <td>
                    {item.description || '-'}
                    {receiptCountByItem[item.id] > 0 ? (
                      <span style={{ marginLeft: '8px', color: 'var(--text-secondary)' }}>
                        📎 {receiptCountByItem[item.id]}
                      </span>
                    ) : canSubmit && (
                      <span style={{ marginLeft: '8px', color: 'var(--status-warning)' }}>
                        no receipt
                      </span>
                    )}
                  </td>
                  <td>{formatAmount(item.amount)}</td>
                  {canEdit && (
                    <td>
                      <button className="btn-small" onClick={() => openEditItem(item)}>Edit</button>
                      <button className="btn-small btn-danger" onClick={() => handleItemDelete(item)}>Delete</button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="page-header" style={{ marginTop: '24px', marginBottom: '8px' }}>
        <h2 style={{ margin: 0 }}>Receipts</h2>
        {canEdit && (
          <>
            <input
              ref={receiptInputRef}
              type="file"
              accept={RECEIPT_ACCEPT}
              style={{ display: 'none' }}
              onChange={handleReceiptPick}
            />
            <button
              className="btn-primary"
              disabled={uploadingReceipt}
              onClick={() => receiptInputRef.current && receiptInputRef.current.click()}
            >
              {uploadingReceipt ? 'Uploading...' : '+ Add Receipt'}
            </button>
            <button className="btn-small" onClick={() => setShowQrModal(true)}>
              📱 Scan with phone
            </button>
          </>
        )}
      </div>

      {receipts.length === 0 ? (
        <div className="loading">
          <p>No receipts attached.</p>
        </div>
      ) : (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>File</th>
                <th>Linked item</th>
                <th>Uploaded</th>
                <th>Size</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {receipts.map((receipt) => (
                <tr key={receipt.id}>
                  <td>{receipt.original_filename || 'Receipt'}</td>
                  <td>
                    {receipt.line_item_id && itemsById[receipt.line_item_id]
                      ? itemSummary(itemsById[receipt.line_item_id])
                      : <span style={{ color: 'var(--text-secondary)' }}>—</span>}
                  </td>
                  <td>{formatDate(receipt.created_at)}</td>
                  <td>{formatBytes(receipt.file_size)}</td>
                  <td>
                    <button className="btn-small" onClick={() => handleReceiptDownload(receipt)}>Download</button>
                    {canEdit && (
                      <>
                        <button
                          className="btn-small"
                          onClick={() => { setLinkSelection(receipt.line_item_id || ''); setLinkTarget(receipt); }}
                        >
                          {receipt.line_item_id ? 'Change link' : 'Link'}
                        </button>
                        <button className="btn-small btn-danger" onClick={() => setReceiptToDelete(receipt)}>Delete</button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      </>
      )}

      {pendingReceipt && (
        <div className="modal-overlay" onClick={() => !uploadingReceipt && setPendingReceipt(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>Add Receipt</h2>
            <p style={{ marginBottom: '16px' }}>{pendingReceipt.file.name}</p>
            <div className="form-group">
              <label>Link to line item (optional)</label>
              <select value={pendingLinkItem} onChange={(e) => setPendingLinkItem(e.target.value)}>
                <option value="">No specific item</option>
                {items.map((it) => (
                  <option key={it.id} value={it.id}>{itemOptionLabel(it)}</option>
                ))}
              </select>
            </div>
            <div className="modal-actions">
              <button type="button" onClick={() => setPendingReceipt(null)}>Cancel</button>
              <button type="button" className="btn-primary" disabled={uploadingReceipt} onClick={handleReceiptUpload}>
                {uploadingReceipt ? 'Uploading...' : 'Upload'}
              </button>
            </div>
          </div>
        </div>
      )}

      {linkTarget && (
        <div className="modal-overlay" onClick={() => !linking && setLinkTarget(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>Link Receipt</h2>
            <p style={{ marginBottom: '16px' }}>{linkTarget.original_filename || 'Receipt'}</p>
            <div className="form-group">
              <label>Line item</label>
              <select value={linkSelection} onChange={(e) => setLinkSelection(e.target.value)}>
                <option value="">No specific item</option>
                {items.map((it) => (
                  <option key={it.id} value={it.id}>{itemOptionLabel(it)}</option>
                ))}
              </select>
            </div>
            <div className="modal-actions">
              <button type="button" onClick={() => setLinkTarget(null)}>Cancel</button>
              <button type="button" className="btn-primary" disabled={linking} onClick={handleReceiptLink}>
                {linking ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {decisionModal && (
        <div className="modal-overlay" onClick={() => !deciding && setDecisionModal(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>{decisionModal === 'approve' ? 'Approve Report' : 'Reject Report'}</h2>
            <div className="form-group">
              <label>{decisionModal === 'approve' ? 'Notes (optional)' : 'Notes *'}</label>
              <textarea
                value={decisionNotes}
                onChange={(e) => setDecisionNotes(e.target.value)}
                rows={3}
                autoFocus
                placeholder={decisionModal === 'approve'
                  ? 'Optional note for the record'
                  : 'What needs to change before resubmission?'}
              />
            </div>
            {decisionModal === 'reject' && (
              <p style={{ color: 'var(--text-secondary)', marginBottom: '16px' }}>
                Rejection notes are shown to the employee.
              </p>
            )}
            <div className="modal-actions">
              <button type="button" onClick={() => setDecisionModal(null)}>Cancel</button>
              <button
                type="button"
                className={decisionModal === 'reject' ? 'btn-primary btn-danger' : 'btn-primary'}
                disabled={deciding || (decisionModal === 'reject' && !decisionNotes.trim())}
                onClick={handleDecision}
              >
                {decisionModal === 'approve'
                  ? (deciding ? 'Approving...' : 'Approve')
                  : (deciding ? 'Rejecting...' : 'Reject Report')}
              </button>
            </div>
          </div>
        </div>
      )}

      {breakGlass && (
        <div className="modal-overlay" onClick={() => !breaking && setBreakGlass(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>{breakGlass === 'approve' ? 'Break-Glass Self-Approval' : 'Break-Glass Self-Pay'}</h2>
            <p style={{ marginBottom: '16px' }}>
              Separation of duties is the norm — a second decision-holder should
              decide this report. If no one else is available, you may proceed.
            </p>
            <div className="form-group">
              <label>Reason *</label>
              <textarea
                value={glassReason}
                onChange={(e) => setGlassReason(e.target.value)}
                rows={3}
                autoFocus
                placeholder="Why is a second decision-holder unavailable?"
              />
            </div>
            {breakGlass === 'pay' && (
              <div className="form-group">
                <label>Check number (optional)</label>
                <input
                  type="text"
                  value={payReference}
                  onChange={(e) => setPayReference(e.target.value)}
                  maxLength={50}
                  placeholder="e.g. 4521"
                />
              </div>
            )}
            <p style={{ color: 'var(--text-secondary)', marginBottom: '16px' }}>
              This report will be permanently marked SELF-APPROVED and other
              approvers will be notified.
            </p>
            <div className="modal-actions">
              <button type="button" onClick={() => setBreakGlass(null)}>Cancel</button>
              <button
                type="button"
                className="btn-primary btn-danger"
                disabled={breaking || !glassReason.trim()}
                onClick={handleBreakGlass}
              >
                {breaking ? 'Working...' : breakGlass === 'approve' ? 'Self-Approve' : 'Self-Pay'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showVoidModal && (
        <div className="modal-overlay" onClick={() => !voiding && setShowVoidModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>Void Report</h2>
            <div className="form-group">
              <label>Reason *</label>
              <textarea
                value={voidReason}
                onChange={(e) => setVoidReason(e.target.value)}
                rows={3}
                autoFocus
                placeholder="Why is this paid report being voided?"
              />
            </div>
            <p style={{ color: 'var(--text-secondary)', marginBottom: '16px' }}>
              Voiding removes this report from totals and exports. The report and
              its history remain visible. This cannot be undone.
            </p>
            <div className="modal-actions">
              <button type="button" onClick={() => setShowVoidModal(false)}>Cancel</button>
              <button
                type="button"
                className="btn-primary btn-danger"
                disabled={voiding || !voidReason.trim()}
                onClick={handleVoid}
              >
                {voiding ? 'Voiding...' : 'Void Report'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showPayConfirm && (
        <div className="modal-overlay" onClick={() => setShowPayConfirm(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>Mark as Paid</h2>
            <p style={{ marginBottom: '16px' }}>
              Mark "{report.title}" as paid? This records the reimbursement as complete.
            </p>
            <div className="form-group">
              <label>Check number (optional)</label>
              <input
                type="text"
                value={payReference}
                onChange={(e) => setPayReference(e.target.value)}
                maxLength={50}
                autoFocus
                placeholder="e.g. 4521"
              />
            </div>
            <div className="modal-actions">
              <button type="button" onClick={() => setShowPayConfirm(false)}>Cancel</button>
              <button type="button" className="btn-primary" disabled={paying} onClick={handlePay}>
                {paying ? 'Marking...' : 'Mark as Paid'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showRefModal && (
        <div className="modal-overlay" onClick={() => !savingRef && setShowRefModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>Edit check number</h2>
            <div className="form-group">
              <label>Check number</label>
              <input
                type="text"
                value={refDraft}
                onChange={(e) => setRefDraft(e.target.value)}
                maxLength={50}
                autoFocus
                placeholder="e.g. 4521"
              />
            </div>
            <p style={{ color: 'var(--text-secondary)', marginBottom: '16px' }}>
              Correct the check number recorded for this payment. The change is
              recorded in the report's history. Leave blank to clear it.
            </p>
            <div className="modal-actions">
              <button type="button" onClick={() => setShowRefModal(false)}>Cancel</button>
              <button type="button" className="btn-primary" disabled={savingRef} onClick={handleSaveReference}>
                {savingRef ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showDeleteConfirm && (
        <div className="modal-overlay" onClick={() => setShowDeleteConfirm(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>Delete Draft</h2>
            <p style={{ marginBottom: '16px' }}>
              Delete "{report.title}"? Its line items and receipts will be permanently removed. This can't be undone.
            </p>
            <div className="modal-actions">
              <button type="button" onClick={() => setShowDeleteConfirm(false)}>Cancel</button>
              <button
                type="button"
                className="btn-primary btn-danger"
                disabled={deletingReport}
                onClick={handleReportDelete}
              >
                {deletingReport ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {receiptToDelete && (
        <div className="modal-overlay" onClick={() => setReceiptToDelete(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>Delete Receipt</h2>
            <p style={{ marginBottom: '16px' }}>
              Delete "{receiptToDelete.original_filename || 'this receipt'}"? This can't be undone.
            </p>
            <div className="modal-actions">
              <button type="button" onClick={() => setReceiptToDelete(null)}>Cancel</button>
              <button
                type="button"
                className="btn-primary btn-danger"
                disabled={deletingReceipt}
                onClick={handleReceiptDelete}
              >
                {deletingReceipt ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showItemModal && (
        <LineItemModal
          editingItem={editingItem}
          form={itemForm}
          setForm={setItemForm}
          categories={categories}
          saving={savingItem}
          onSubmit={handleItemSubmit}
          onClose={() => setShowItemModal(false)}
        />
      )}

      {showTitleModal && (
        <div className="modal-overlay" onClick={() => setShowTitleModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>Edit Title</h2>
            <form onSubmit={handleTitleSave}>
              <div className="form-group">
                <label>Title *</label>
                <input
                  type="text"
                  value={titleDraft}
                  onChange={(e) => setTitleDraft(e.target.value)}
                  autoFocus
                  required
                />
              </div>
              <div className="modal-actions">
                <button type="button" onClick={() => setShowTitleModal(false)}>Cancel</button>
                <button type="submit" className="btn-primary" disabled={savingTitle || !titleDraft.trim()}>
                  {savingTitle ? 'Saving...' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {warnings && (
        <WarningsModal warnings={warnings} onClose={() => setWarnings(null)} />
      )}

      {showQrModal && (
        <QrHandoffModal
          reportId={id}
          onClose={() => setShowQrModal(false)}
          onCompleted={() => {
            setShowQrModal(false);
            toast.success('Receipt received from phone');
            fetchReport();
          }}
        />
      )}
    </div>
  );
};

export default ExpenseDetail;
